import { getAnalyticsService, AnalyticsEvent } from './index';

/**
 * How a proposed merge ended, as the usage tracking knows it. `timed_out` and `merged_in_upay_only` are the two
 * outcomes where UPortal and UPay may disagree, so they are told apart from a plain failure.
 */
export type MergeTrackedOutcome = 'merged' | 'failed' | 'timed_out' | 'merged_in_upay_only';

export interface MergeOutcomeToTrack {
  outcome: MergeTrackedOutcome;
  daysCount: number;
  errorKeys: string[];
  /** The time from confirmation to this merge's outcome, as the API measured it. Without it the browser's clock is used. */
  durationMs?: number;
}

export interface MergeTracker {
  trackClicked(selectedCount: number, employeesCount: number): void;
  trackConfirmed(proposedMergesCount: number, deselectedCount: number): void;
  trackOutcome(result: MergeOutcomeToTrack): void;
}

/**
 * The tracking plan of US 192: only names, counts, flags and ids go to the analytics service - no employee names and
 * no free text. Tracking must never get in the way of a merge, so a failing analytics service is swallowed.
 */
export const createMergeTracker = (): MergeTracker => {
  let confirmedAt: number | null = null;

  const track = (event: AnalyticsEvent, properties: Record<string, unknown>): void => {
    try {
      getAnalyticsService().trackEvent(event, properties);
    } catch {
      // the merge goes on without its tracking
    }
  };

  const trackClicked = (selectedCount: number, employeesCount: number): void => {
    track(AnalyticsEvent.MergeClicked, { selected_count: selectedCount, employees_count: employeesCount });
  };

  const trackConfirmed = (proposedMergesCount: number, deselectedCount: number): void => {
    confirmedAt = Date.now();
    track(AnalyticsEvent.MergeConfirmed, {
      proposed_merges_count: proposedMergesCount,
      deselected_count: deselectedCount,
    });
  };

  const trackOutcome = ({ outcome, daysCount, errorKeys, durationMs }: MergeOutcomeToTrack): void => {
    const measuredHere = confirmedAt === null ? 0 : Date.now() - confirmedAt;
    const properties = {
      days_count: daysCount,
      outcome,
      duration_ms: durationMs && durationMs > 0 ? durationMs : measuredHere,
      ...(errorKeys.length > 0 && { error_keys: errorKeys }),
    };

    track(outcome === 'merged' ? AnalyticsEvent.MergeCompleted : AnalyticsEvent.MergeFailed, properties);
  };

  return { trackClicked, trackConfirmed, trackOutcome };
};
